<template>

  <div>

    <planogram-list-add-new
      :is-add-new-planogram-sidebar-active.sync="isAddNewPlanogramSidebarActive"
      :role-options="roleOptions"
      :company-options="companyOptions"
      @refetch-data="refetchData"
    />

    <!-- Filters -->
    <planograms-list-filters
      :role-filter.sync="roleFilter"
      :company-filter.sync="companyFilter"
      :status-filter.sync="statusFilter"
      :role-options="roleOptions"
      :company-options="companyOptions"
      :status-options="statusOptions"
    />

    <!-- Table Container Card -->
    <b-card
      no-body
      class="mb-0"
    >

      <div class="m-2">

        <!-- Table Top -->
        <b-row>

          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label>{{ $t('common.show') }}</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label>{{ $t('common.entries') }}</label>
          </b-col>

          <!-- Search -->
          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                :placeholder="$t('common.search')"
              />
              <b-button
                variant="primary"
                @click="$router.push({ name: 'apps-planograms-edit', params: { id: 'new', clone: false }})"
              >
                <span class="text-nowrap">{{ $t('planograms.add_planogram') }}</span>
              </b-button>

              <b-dropdown
                variant="link"
                no-caret
                :right="$store.state.appConfig.isRTL"
              >

                <template #button-content>
                  <feather-icon
                    icon="DownloadIcon"
                    size="16"
                    class="align-middle text-body"
                  />
                </template>

                <b-dropdown-item @click="fetchPlanogramsFile()">
                  <feather-icon icon="AlignJustifyIcon" />
                  <span class="align-middle ml-50">{{ $t('common.save_xlsx') }}</span>
                </b-dropdown-item>

                <b-dropdown-item
                  :disabled="true"
                  @click="fetchSalesFile('pdf')"
                >
                  <feather-icon icon="BookIcon" />
                  <span class="align-middle ml-50">{{ $t('common.save_pdf') }}</span>
                </b-dropdown-item>
              </b-dropdown>

              <b-button
                variant="flat-info"
                class="btn-icon rounded-circle"
                @click="refetchData"
              >
                <feather-icon
                  icon="RefreshCwIcon"
                  size="18"
                />
              </b-button>

            </div>
          </b-col>
        </b-row>

      </div>

      <b-table
        ref="refPlanogramListTable"
        class="position-relative"
        :items="fetchPlanograms"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No matching records found"
        :sort-desc.sync="isSortDirDesc"
      >

        <!-- Column: Planogram -->
        <template #cell(name)="data">
          <b-media vertical-align="center">
            <b-link
              :to_="{ name: 'apps-planograms-view', params: { id: data.item.id } }"
              :to="{ name: 'apps-planograms-edit', params: { id: data.item.id, clone: false } }"
              class="font-weight-bold d-block text-nowrap"
            >
              {{ data.item.name }}
            </b-link>
            <!--small class="text-muted">@{{ data.item.planogramname }}</small-->
          </b-media>
        </template>

        <!-- Column: Role -->
        <template #cell(role_id)="data">
          <div class="text-nowrap">
            <feather-icon
              :icon="resolvePlanogramRoleIcon(data.item.role)"
              size="18"
              class="mr-50"
              :class="`text-${resolvePlanogramRoleVariant(data.item.role)}`"
            />
            <span class="align-text-top text-capitalize">{{ data.item.role }} {{ resolvePlanogramRoleText(data.item.roleId) }}</span>
          </div>
        </template>

        <!-- Column: Company -->
        <template #cell(company_id)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">{{ resolvePlanogramCompanyText(data.item.companyId) }}</span>
          </div>
        </template>

        <!-- Column: Status -->
        <template #cell(status)="data">
          <b-badge
            pill
            :variant="`light-${resolvePlanogramStatusVariant(data.item.status)}`"
            class="text-capitalize"
          >
            {{ data.item.status }}
          </b-badge>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <!--b-dropdown-item :to="{ name: 'apps-planograms-view', params: { id: data.item.id } }">
              <feather-icon icon="FileTextIcon" />
              <span class="align-middle ml-50">Details</span>
            </b-dropdown-item-->

            <b-dropdown-item :to="{ name: 'apps-planograms-edit', params: { id: data.item.id }}">
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">{{ $t('common.edit') }}</span>
            </b-dropdown-item>

            <b-dropdown-item @click="deletePlanogram(data.item)">
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50">{{ $t('common.delete') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>

      </b-table>
      <div class="mx-2 mb-2">
        <b-row>

          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">{{ $t('common.showing') }} {{ dataMeta.from }} {{ $t('common.to') }} {{ dataMeta.to }} {{ $t('common.of') }} {{ dataMeta.of }} {{ $t('common.entries') }}</span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >

            <b-pagination
              v-model="currentPage"
              :total-rows="totalPlanograms"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>

          </b-col>

        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  // BCard, BRow, BCol, BFormInput, BButton, BTable, BMedia, BAvatar, BLink,
  BCard, BRow, BCol, BFormInput, BButton, BTable, BMedia, BLink,
  BBadge, BDropdown, BDropdownItem, BPagination,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { ref, onUnmounted } from '@vue/composition-api'
import { avatarText } from '@core/utils/filter'

import store from '@/store'
import PlanogramsListFilters from './PlanogramsListFilters.vue'
import usePlanogramsList from './usePlanogramsList'
import planogramStoreModule from '../planogramStoreModule'
import PlanogramListAddNew from './PlanogramListAddNew.vue'

export default {
  components: {
    PlanogramsListFilters,
    PlanogramListAddNew,

    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    // BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,

    vSelect,
  },
  setup() {
    const USER_APP_STORE_MODULE_NAME = 'app-planogram'

    // Register module
    if (!store.hasModule(USER_APP_STORE_MODULE_NAME)) store.registerModule(USER_APP_STORE_MODULE_NAME, planogramStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(USER_APP_STORE_MODULE_NAME)) store.unregisterModule(USER_APP_STORE_MODULE_NAME)
    })

    const isAddNewPlanogramSidebarActive = ref(false)

    /*

    const roleOptions = [
      { label: 'Admin', value: 'admin' },
      { label: 'Author', value: 'author' },
      { label: 'Editor', value: 'editor' },
      { label: 'Maintainer', value: 'maintainer' },
      { label: 'Subscriber', value: 'subscriber' },
    ]

    /*
    const companyOptions = [
      { label: 'Basic', value: 'basic' },
      { label: 'Company', value: 'company' },
      { label: 'Enterprise', value: 'enterprise' },
      { label: 'Team', value: 'team' },
    ]
    */

    const statusOptions = [
      // { label: 'Pending', value: 'pending' },
      { label: 'Active', value: 'active' },
      { label: 'Inactive', value: 'inactive' },
    ]

    const {
      fetchPlanograms,
      fetchPlanogramsFile,
      tableColumns,
      perPage,
      currentPage,
      totalPlanograms,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refPlanogramListTable,
      refetchData,

      // UI
      resolvePlanogramRoleVariant,
      resolvePlanogramRoleIcon,
      resolvePlanogramRoleText,
      resolvePlanogramStatusVariant,
      resolvePlanogramCompanyText,

      // Extra Filters
      roleFilter,
      companyFilter,
      statusFilter,

      roleOptions,
      companyOptions,

      deletePlanogram,

    } = usePlanogramsList()

    return {

      // Sidebar
      isAddNewPlanogramSidebarActive,

      fetchPlanograms,
      fetchPlanogramsFile,
      tableColumns,
      perPage,
      currentPage,
      totalPlanograms,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refPlanogramListTable,
      refetchData,

      // Filter
      avatarText,

      // UI
      resolvePlanogramRoleVariant,
      resolvePlanogramRoleIcon,
      resolvePlanogramRoleText,
      resolvePlanogramStatusVariant,
      resolvePlanogramCompanyText,

      roleOptions,
      companyOptions,
      statusOptions,

      // Extra Filters
      roleFilter,
      companyFilter,
      statusFilter,

      deletePlanogram,

    }
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-sweetalert.scss';
</style>
